import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {combineLatest, Observable, throwError} from 'rxjs';
import {catchError, filter, finalize, shareReplay, switchMap, take} from 'rxjs/operators';
import {Store} from '@ngrx/store';
import {ApiService} from '@core/services/api.service';
import {selectAuthState, selectUserAuthentication, selectUserToken} from '../../store/auth/auth.selectors';
import {AuthState} from '../../store/auth/auth.state';
import * as AuthActions from '../../store/auth/auth.actions';

/**
 * JwtInterceptor
 *
 * Security improvements (OWASP A03 / A07):
 * - Reads the access token exclusively from the NgRx in-memory store (never localStorage).
 * - Adds withCredentials: true so the browser transparently sends the HttpOnly refresh cookie.
 * - On 401/403, triggers a single token refresh and retries the original request.
 *   A shared pending-refresh observable prevents parallel refresh storms.
 *
 * Two in-memory token sources are consulted:
 *   1. `userAuthentication.token` — the long-lived session token set after /login or /refresh.
 *   2. `userToken.token`          — the short-lived bootstrap token minted by /create,
 *                                   /validate-code, /resend-validation-code, /request-password-reset.
 *
 * Precedence normally favours the session token, falling back to the bootstrap token
 * when no session exists (the common anonymous signup/reset case). BUT the backend
 * gates a few follow-up endpoints (/validate-code, /resend-validation-code,
 * /set-password) to a VALIDATION_CODE-type token; a session (SCHOOL_AUTHENTICATION)
 * token is the wrong type there and is rejected with a 401. This happens when a user
 * who is already logged in creates/validates another school. For those endpoints we
 * therefore prefer the bootstrap token — see {@link requiresBootstrapToken}.
 */
@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  // Shared while a refresh is settling, so concurrent 401s trigger a single refresh. Reset in
  // finalize, which fires on completion AND on teardown: an unsubscribed waiter (an NgRx effect
  // cancelled by switchMap, a component navigating away) can never leave the guard latched.
  private refreshSettled$: Observable<AuthState> | null = null;

  constructor(private store: Store, private apiService: ApiService) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Refresh and logout rely solely on the HttpOnly cookie.
    // Injecting an Authorization header (even an empty one) causes the backend
    // JWT filter to reject the request before it reads the cookie.
    if (this.isRefreshRequest(request)) {
      return next.handle(request.clone({ withCredentials: true }));
    }

    return combineLatest([
      this.store.select(selectUserAuthentication),
      this.store.select(selectUserToken)
    ]).pipe(
      take(1),
      switchMap(([auth, userToken]) => {
        // VALIDATION_CODE-gated endpoints must use the freshly minted bootstrap token,
        // never a lingering session token (wrong type → 401). Everywhere else prefer
        // the session token, falling back to the bootstrap token for anonymous flows.
        const token = this.requiresBootstrapToken(request)
          ? (userToken?.token ?? auth?.token ?? null)
          : (auth?.token ?? userToken?.token ?? null);
        const cloned = this.addCredentials(request, token);
        return next.handle(cloned).pipe(
          catchError((error: HttpErrorResponse) => {
            // A 401 from a public auth endpoint (login, account creation, password
            // reset, …) means bad credentials/input, not an expired access token.
            // Refreshing is impossible (no session yet) and would deadlock, so let
            // the error propagate to the caller.
            if ((error.status === 401 || error.status === 403) && !this.isPublicAuthRequest(request)) {
              return this.handle401(request, next);
            }
            return throwError(() => error);
          })
        );
      })
    );
  }

  private addCredentials(request: HttpRequest<any>, token: string | null): HttpRequest<any> {
    const headers = this.apiService.getHeaders();
    return request.clone({
      setHeaders: {
        ...headers,
        'Authorization': token ? `Bearer ${token}` : ''
      },
      withCredentials: true
    });
  }

  private handle401(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.refreshSettled$) {
      this.store.dispatch(AuthActions.refreshToken());
      // Wait until the refresh settles (loading flips back to false). The dispatch above is
      // fire-and-forget, so the refresh HTTP call proceeds even if every waiter unsubscribes.
      this.refreshSettled$ = this.store.select(selectAuthState).pipe(
        filter((state) => !state.loading),
        take(1),
        finalize(() => this.refreshSettled$ = null),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    }

    // Retry with the new token once the refresh settles, or — if it failed and left us without
    // a token — log out and surface the error. Never park indefinitely on a token that may
    // never arrive.
    return this.refreshSettled$.pipe(
      switchMap((state) => {
        const token = state.authentication?.token ?? null;
        if (token) {
          return next.handle(this.addCredentials(request, token)).pipe(
            catchError((retryError: HttpErrorResponse) => {
              // The retried request was rejected with the fresh token: the session is
              // definitively gone, so log out instead of surfacing a raw server error.
              if (retryError.status === 401 || retryError.status === 403) {
                this.store.dispatch(AuthActions.logout());
                return throwError(() => new Error('Session expired. Please sign in again.'));
              }
              return throwError(() => retryError);
            })
          );
        }
        this.store.dispatch(AuthActions.logout());
        return throwError(() => new Error('Session expired. Please sign in again.'));
      })
    );
  }

  private isRefreshRequest(request: HttpRequest<any>): boolean {
    return request.url.includes('/refresh') || request.url.includes('/logout');
  }

  // Endpoints the backend restricts to a VALIDATION_CODE-type token. These follow-up
  // calls in the signup / password-reset flow must carry the short-lived bootstrap
  // token from `userToken`, not a session access token — otherwise the backend JWT
  // filter rejects the wrong-type token with a 401 (which happens when the user is
  // already logged in as another school's admin).
  private requiresBootstrapToken(request: HttpRequest<any>): boolean {
    return [
      '/validate-code',
      '/resend-validation-code',
      '/set-password'
    ].some((path) => request.url.includes(path));
  }

  // Unauthenticated endpoints where a 401 signals invalid credentials/input
  // rather than an expired token — refreshing is pointless and would deadlock.
  //
  // These are all endpoints on the public `school-account` service base. The
  // ambiguous verbs (`create`, `invitation`) are qualified with that base so
  // they don't over-match authenticated endpoints that happen to end in the
  // same word — e.g. `task-definition/create`, whose 401 (expired token) MUST
  // go through refresh-and-retry, not be silently surfaced to the caller.
  private isPublicAuthRequest(request: HttpRequest<any>): boolean {
    return [
      '/login',
      'school-account/create',
      '/validate-code',
      '/resend-validation-code',
      '/request-password-reset',
      '/set-password',
      'school-account/invitation'
    ].some((path) => request.url.includes(path));
  }
}